/**
 * economyActions — wspólny wrapper dla granularnych akcji ekonomii (sesja 35B).
 *
 * Modale (`EggShopModal`, `EggInventoryModal`, `UpgradesModal`, `BiomeMapModal`,
 * `StarterPetSelectorModal`) wołały wcześniej `saveManager.update(recipe)` =
 * blob `PUT /saves/me`. Po sesji 35A PUT `skipEntities` celowo NIE zapisuje
 * pets/eggs/upgrades/biomów — granularne endpointy (`economyApi`) to jedyna
 * droga. Wszystkie granularne endpointy (economy + stalls + rewards) zwracają
 * DELTĘ `{ changes }`, którą aplikujemy przez drift-guard jako źródło prawdy.
 *
 * **Drain autosave** — granularny zapis ekonomii i blob PUT autosave dzielą
 * jedno pole: `coins`. Bez synchronizacji:
 *   1. gracz zebrał monety w świecie → autosave `dirty`, PUT czeka w debounce
 *   2. granularny call odejmuje koszt od `coins` SERWERA (starszych niż świat)
 *   3. albo: zaległy PUT ląduje PO granularnym → nadpisuje odjęcie kosztu
 * Dlatego `runEconomyAction`:
 *   1. `flushAutoSave()` — wypycha zaległy PUT (serwer dostaje monety ze świata)
 *   2. `pauseAutoSave()` — blokuje autosave na czas requestu
 *   3. wykonuje granularny call
 *   4. `applyEconomyDelta`/`adoptServerState` + sync store ze zwróconego wyniku
 *   5. `resumeAutoSave()` — wznawia (zawsze, też po błędzie)
 *
 * **Globalna serializacja** (Codex 35B HIGH 1) — wszystkie `runEconomyAction`
 * idą przez jeden łańcuch (`economyActionLock`), wykonują się SEKWENCYJNIE.
 * Bez tego dwie równoległe akcje (np. `UpgradesModal` ma mutex `inFlightRef`
 * per-upgrade, więc pozwala kupić 2 RÓŻNE upgrade'y naraz) mogłyby zaadoptować
 * save'y w kolejności zakończenia requestów, nie commitów backendu — starsza
 * odpowiedź nadpisałaby nowszy server-state w kliencie.
 *
 * Pełna autorytatywność `coins` (świat) przyjdzie w 4.D.3 (`/world/tick`).
 */

import { saveManager, type PlayerSave } from '@/save';
import { useGameStore } from '@/ui/store/gameStore';
import { eventBus } from '@/shared/EventBus';
import { ApiError, saveApi, type EconomyDelta } from '@/net/api';
import {
  runWithPreflight,
  withResyncFlag,
  EconomyResyncFailedError,
} from '@/domain/economy/economyPreflight';
import {
  flushAutoSave,
  pauseAutoSave,
  resumeAutoSave,
  getPendingCoinsDelta,
} from '@/save/AutoSaveBridge';
import { addCoins } from '@/domain/economy/coins';

/**
 * Synchronizuje Zustand store + Phaser HUD z autorytatywnym save'em z serwera.
 * Wołane po każdej udanej akcji ekonomii (w `runEconomyAction`).
 */
export function syncStoresFromSave(save: PlayerSave): void {
  const store = useGameStore.getState();
  // coins = stan serwera + monety zebrane w świecie jeszcze NIE wysłane do
  // `/world/tick` (sesja 4.D.3 reconciliation). Bez tego inflight earnings
  // zniknęłyby z HUD do następnego ticka (flicker / chwilowa „utrata").
  const displayCoins = addCoins(save.currencies.coins, getPendingCoinsDelta());
  store.setCoins(displayCoins);
  // Etap 5, 5.0.A — gems server-authoritative (drop z dest-crystal w 5.0.B,
  // daily reward 5.0.C, merge bonus 5.0.D, NPC sell 5.0.E). Brak inflight
  // delta jak przy coins (drop jest losowany na serwerze, klient nie wie
  // ile gems zarobił między tickami — czeka na response).
  store.setGems(save.currencies.gems);
  // Eventy (EV-1a) — portfel walut eventowych (server-authoritative, endpointy
  // eventu). `?? {}` dla starych save'ów sprzed v4 zanim migracja je dotknie.
  store.setEventCurrencies(save.eventCurrencies ?? {});
  // EV-3: stan eventów (album odkrycia/claim) — reflektor save.events.
  store.setEvents(save.events ?? {});
  // Odblokowane skiny (claim albumu dorzuca letnie awatary) + bieżący skin —
  // selektor „Wygląd" odświeża się po claimie bez resyncu.
  store.setUnlockedSkins(save.profile.unlockedSkins ?? [save.profile.avatar.skinId]);
  store.setAvatarSkinId(save.profile.avatar.skinId);
  store.setEggInventory(save.eggs);
  store.setPetInventory(save.inventory);
  store.setEquippedUids(save.equipped);
  store.setUpgrades(save.upgrades);
  store.setCurrentBiomeId(save.currentBiomeId);
  store.setStarterPetsChosen(save.flags.starterPetsChosen === true);
  // Mikstury — plecak + aktywne buffy (server-authoritative, /potions/*).
  store.setPotions(save.potions);
  store.setActiveEffects(save.activeEffects);
  // Phaser trzyma `coinTotal` lokalnie (HUD) — wyrównaj do server-state.
  eventBus.emit('coins:setExternal', { newAmount: displayCoins });
}

/**
 * Łańcuch serializujący granularne akcje ekonomii (Codex 35B HIGH 1). Każde
 * `runEconomyAction` dolinkowuje się na koniec — flush/pause/call/adopt/resume
 * jednej akcji w całości kończy się PRZED startem kolejnej.
 */
/**
 * Kod błędu preflightu — mutacja NIE poszła na serwer, bo lokalny stan gry był
 * niepewny. Modale mapują go na `ECONOMY_RESYNC_MESSAGE`, żeby na każdym
 * ekranie brzmiało to tak samo (Codex V-4 FIXES⁴ HIGH 1).
 */
export const ECONOMY_RESYNC_CODE = 'economy_resync_required';
/** Ponowienie jest BEZPIECZNE: preflight próbuje resyncu przed każdą próbą. */
export const ECONOMY_RESYNC_MESSAGE =
  'Gra straciła kontakt z zapisem. Nic nie zostało wydane — spróbuj ponownie za chwilę.';

let economyActionLock: Promise<unknown> = Promise.resolve();

/**
 * Wykonuje granularną akcję ekonomii — z drain autosave (patrz nagłówek pliku)
 * ORAZ globalną serializacją przez `economyActionLock`. `action` to wywołanie
 * `economyApi.*`/`stallApi.*`/`rewardsApi.*` zwracające `{ changes, ... }` (delta).
 * Gałąź `{ save, ... }` została defensywnie (full-save adopt). Po sukcesie wynik jest aplikowany
 * przez drift-guard; ewentualny błąd (`ApiError`) propaguje do wołającego (modal
 * mapuje na komunikat) — autosave i tak zostaje wznowiony, a łańcuch
 * nieprzerwany (`.catch` na poprzednim ogniwie).
 */
export function runEconomyAction<T extends { save_rev: number }>(
  action: () => Promise<T>,
): Promise<T> {
  // `.catch` na poprzednim ogniwie — błąd jednej akcji NIE blokuje kolejnych.
  const run = economyActionLock.catch(() => {}).then(async (): Promise<T> => {
    await flushAutoSave();
    pauseAutoSave();
    try {
      // PREFLIGHT (Codex V-4 FIXES⁴ HIGH 1): dopóki save jest oznaczony jako
      // niepewny, NIE WOLNO puścić żadnej mutacji ekonomii — nie tylko tej ze
      // Skarbca. Jeśli nieudany hurt naprawdę wykonał się na serwerze, lokalna
      // rewizja jest w tyle; kolejna akcja zostałaby zatwierdzona przez swój
      // endpoint, a klient wykryłby lukę dopiero przy nakładaniu delty i — przy
      // wciąż padającym `GET /saves/me` — pokazał odrzucenie akcji, która na
      // serwerze już zaszła. Gracz ponawiałby „nieudany" zakup/merge/sprzedaż,
      // wydając walutę wielokrotnie.
      //
      // Bramka MUSI być tutaj: wewnątrz `economyActionLock` i przed `action()`.
      // `doResync()` jest lock-free właśnie po to, by dało się je stąd wywołać.
      // Samą kolejność trzyma `runWithPreflight` — bez zależności, więc pilnuje
      // jej stały test `tests/economyPreflight.test.mjs`.
      const result = await runWithPreflight({
        isBlocked: () => useGameStore.getState().economyResyncRequired,
        resync: doResync,
        action,
      }).catch((err: unknown) => {
        if (err instanceof EconomyResyncFailedError) {
          throw new ApiError({
            status: 0,
            code: ECONOMY_RESYNC_CODE,
            message: ECONOMY_RESYNC_MESSAGE,
            ...(err.cause instanceof ApiError ? { field: err.cause.code } : {}),
          });
        }
        throw err;
      });
      // Wszystkie granularne endpointy (economy + stalls + rewards) zwracają DELTĘ
      // `{ changes }` (drift-guard `applyEconomyDelta`). Gałąź `{ save }` została
      // defensywnie (full-save adopt — np. przyszłe/nieskonwertowane ścieżki).
      const r = result as { save?: PlayerSave; changes?: EconomyDelta; save_rev: number };
      if (r.changes !== undefined) {
        await applyEconomyDelta(r.changes, r.save_rev);
      } else if (r.save !== undefined) {
        // HIGH 1: store/HUD TYLKO gdy guard przyjął — stara/spóźniona odpowiedź
        // nie cofnie walut/inventory w Zustand mimo skipu w `current`.
        const applied = await saveManager.adoptServerState(r.save, r.save_rev);
        if (applied) syncStoresFromSave(r.save);
      }
      return result;
    } finally {
      resumeAutoSave();
    }
  });
  economyActionLock = run;
  return run;
}

/**
 * Buduje nowy `PlayerSave` z delty (Faza 1b-2) — immutable add/remove petów/
 * jajek + autorytatywne waluty/staty/equipped z serwera. Czysta funkcja
 * (przekazywana do `SaveManager.applyDelta` pod `updateLock`).
 */
function mutateWithDelta(current: PlayerSave, c: EconomyDelta): PlayerSave {
  let inventory = current.inventory;
  let eggs = current.eggs;
  if (c.removedEggUid) {
    eggs = eggs.filter((e) => e.uid !== c.removedEggUid);
  }
  if (c.removedEggUids && c.removedEggUids.length > 0) {
    const removed = new Set(c.removedEggUids);
    eggs = eggs.filter((e) => !removed.has(e.uid));
  }
  if (c.newEgg) eggs = [...eggs, c.newEgg];
  if (c.newEggs && c.newEggs.length > 0) eggs = [...eggs, ...c.newEggs];
  // Usunięcia petów (1b-3): merge konsumuje N, sell-to-npc usuwa 1.
  if (c.removedPetUid) {
    inventory = inventory.filter((p) => p.uid !== c.removedPetUid);
  }
  if (c.removedPetUids && c.removedPetUids.length > 0) {
    const removedPets = new Set(c.removedPetUids);
    inventory = inventory.filter((p) => !removedPets.has(p.uid));
  }
  // Aktualizacje petów (1b-3): flags lock/favorite — replace po uid (immutable map).
  if (c.updatedPet) {
    const up = c.updatedPet;
    inventory = inventory.map((p) => (p.uid === up.uid ? up : p));
  }
  if (c.updatedPets && c.updatedPets.length > 0) {
    const upMap = new Map(c.updatedPets.map((p) => [p.uid, p]));
    inventory = inventory.map((p) => upMap.get(p.uid) ?? p);
  }
  if (c.newPet) inventory = [...inventory, c.newPet];
  if (c.newPets && c.newPets.length > 0) inventory = [...inventory, ...c.newPets];
  return {
    ...current,
    inventory,
    eggs,
    currencies: c.currencies ? { ...current.currencies, ...c.currencies } : current.currencies,
    // Portfel eventowy — MERGE (delta niesie tylko zmienione waluty, np. muszelki);
    // nie clobbernąć innych walut eventowych. `syncStoresFromSave` odświeży HUD.
    eventCurrencies: c.eventCurrencies
      ? { ...current.eventCurrencies, ...c.eventCurrencies }
      : current.eventCurrencies,
    stats: c.stats ? { ...current.stats, ...c.stats } : current.stats,
    // eggStats — pełne z serwera (hatch/bulk mutują pity); ustaw gdy obecne.
    eggStats: c.eggStats ?? current.eggStats,
    equipped: c.equipped ?? current.equipped,
    // 1b-4: upgrades/biomesUnlocked — pełna autorytatywna wartość z serwera (replace).
    upgrades: c.upgrades ?? current.upgrades,
    biomesUnlocked: c.biomesUnlocked ?? current.biomesUnlocked,
    // Nawigacja między biomami jest zapisywana granularnie; pełny save z dużym
    // inventory nie musi już lecieć z telefonu przy każdym teleportowaniu.
    currentBiomeId: c.currentBiomeId ?? current.currentBiomeId,
    // flags MERGE (nie replace) — delta niesie tylko zmienione klucze (np.
    // starterPetsChosen); nie wolno clobbernąć client-only flag (avatarSelected itd.).
    flags: c.flags ? { ...current.flags, ...c.flags } : current.flags,
    // Mikstury — pełne mapy z serwera (replace gdy obecne w delcie).
    potions: c.potions ?? current.potions,
    activeEffects: c.activeEffects ?? current.activeEffects,
    // EV-3: events (album odkrycia/claim) + profile (odblokowane awatary) — pełna
    // autorytatywna wartość z serwera (replace gdy obecna).
    events: c.events ?? current.events,
    profile: c.profile ?? current.profile,
  };
}

/**
 * Aplikuje deltę akcji ekonomii do `current` + store/HUD przez drift-guard
 * (Faza 1b-2). Gdy `save_rev` ma lukę (multi-tab) → pełny resync `GET /saves/me`
 * (lock-free `doResync` — jesteśmy już w `economyActionLock`, autosave spauzowany,
 * więc NIE wolno wołać `refreshSaveFromServer`, które re-wzięłoby lock = deadlock).
 */
async function applyEconomyDelta(changes: EconomyDelta, saveRev: number): Promise<void> {
  const { applied, gap } = await saveManager.applyDelta(
    (current) => mutateWithDelta(current, changes),
    saveRev,
  );
  if (applied) {
    // Store/HUD z nowego `current` (payload był maleńki; pełny set store to
    // istniejące zachowanie — wirtualizacja listy dojdzie w rundzie perf).
    syncStoresFromSave(saveManager.getCurrent());
  } else if (gap) {
    // Mutacja na serwerze JUŻ się wykonała — nie zamieniamy tego w błąd dla
    // wołającego. Ale jeśli nie umiemy dogonić stanu, KOLEJNA akcja musi być
    // zablokowana, inaczej gracz działa na plecaku, którego gra nie zna
    // (Codex V-4 FIXES⁵ — dotąd nikt tu flagi nie podnosił).
    // Mutacja na serwerze JUŻ się wykonała — nie zamieniamy tego w błąd dla
    // wołającego. Flagę podnosi samo `doResync`, więc tu tylko połykamy błąd.
    await doResync().catch(() => {});
  }
}

/**
 * Pobiera autorytatywny save z serwera (`GET /saves/me`) i adoptuje go jako
 * źródło prawdy — store + Phaser HUD. Używane gdy stan zmienił się SERVER-SIDE
 * bez naszego requestu ekonomii (np. direct trade 5.C.4: realtime wykonał
 * wymianę przez API, browser dostał tylko `trade_completed` i musi się
 * zsynchronizować — pety + 💎 się zmieniły).
 *
 * Idzie przez ten sam łańcuch + flush/pause/resume co `runEconomyAction`, żeby
 * nie wyścigować się z zaległym blob PUT autosave (który mógłby nadpisać świeże
 * pety/gems starym snapshotem klienta).
 */
/**
 * Pełny resync z serwera (`GET /saves/me` + adopt + sync) BEZ `economyActionLock`.
 * Drift-guard: adopt tylko gdy `save_rev` z GET nowszy niż applied (HIGH 1).
 * Resync po direct trade działa, bo trade bumpuje rev obu stron (HIGH 2) → GET
 * zwraca wyższy rev → applied=true.
 *
 * Lock-free, bo wołane też z `applyEconomyDelta` (przy luce rev) — to jest JUŻ
 * wewnątrz `economyActionLock` z spauzowanym autosave; ponowne wzięcie locka
 * (przez `refreshSaveFromServer`) dałoby deadlock.
 */
async function doResync(): Promise<void> {
  // `withResyncFlag` jest JEDYNYM właścicielem obu przejść flagi
  // (Codex V-4 FIXES⁶ HIGH 1). Wcześniej zdejmowanie było tutaj, a podnoszenie
  // rozproszone po wołających — więc awaria resyncu po trade albo po
  // niejednoznacznym merge'u zostawiała grę odblokowaną.
  await withResyncFlag({
    setBlocked: (blocked) => useGameStore.getState().setEconomyResyncRequired(blocked),
    resync: async () => {
      const result = await saveApi.me();
      if (!result.initialized) {
        // Resync „udany technicznie", ale bez save'a. Dla konta, które przed
        // chwilą wykonało operację ekonomii, to NIE jest odzyskany stan.
        throw new EconomyResyncFailedError();
      }
      const applied = await saveManager.adoptServerState(result.save, result.save_rev);
      if (applied) syncStoresFromSave(result.save);
      // `applied === false` też zdejmuje flagę i jest poprawne: rewizja serwera
      // NIE jest nowsza od zaadoptowanej, czyli nie ma czego doganiać.
    },
  });
}


export function refreshSaveFromServer(): Promise<void> {
  const run = economyActionLock.catch(() => {}).then(async (): Promise<void> => {
    await flushAutoSave();
    pauseAutoSave();
    try {
      await doResync();
    } finally {
      resumeAutoSave();
    }
  });
  economyActionLock = run;
  return run;
}
